// ArcaOS Prompt Library — governed AI intelligence layer (Phase 1.7).
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { osFetch } from "../api";
import { Sparkles, Plus, Search, Download, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  approved: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  in_review: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  draft: "text-slate-300 border-white/15 bg-white/5",
};

export function StatusBadge({ status }) {
  return <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${STATUS[status] || STATUS.draft}`} data-testid={`prompt-status-${status}`}>{(status || "draft").replace("_", " ")}</span>;
}

export default function PromptLibrary() {
  const [prompts, setPrompts] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (cat) params.set("category", cat);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    osFetch(`/prompts?${params.toString()}`).then(setPrompts).catch(() => setPrompts([]));
  }, [cat, status, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { osFetch("/prompts/categories").then((d) => setCats(d.categories || [])).catch(() => {}); }, []);

  const exportAll = async () => {
    try {
      const data = await osFetch("/prompts/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "arcaos-prompts.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} prompts`);
    } catch (e) { toast.error(e.message); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text());
      const r = await osFetch("/prompts/import", { method: "POST", body: payload });
      toast.success(`Imported ${r.created} new, ${r.updated} updated (as drafts)`);
      load();
    } catch (err) { toast.error(err.message || "Invalid file"); }
    e.target.value = "";
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="os-prompt-library">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF] flex items-center gap-2"><Sparkles size={13} /> Prompt Library · ArcaCore</p>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-1" data-testid="prompt-lib-title">The intelligence layer</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">Every AI interaction across ArcaCentum resolves through these governed, versioned prompts — approval workflow, evaluation, and full performance metrics.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportAll} className="inline-flex items-center gap-1.5 text-xs text-slate-300 border border-white/10 rounded-xl px-3 py-2.5 hover:text-white hover:border-white/30 transition-colors" data-testid="prompt-export-btn"><Download size={14} /> Export</button>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-300 border border-white/10 rounded-xl px-3 py-2.5 hover:text-white hover:border-white/30 transition-colors cursor-pointer" data-testid="prompt-import-btn"><Upload size={14} /> Import<input type="file" accept="application/json" className="hidden" onChange={importFile} data-testid="prompt-import-input" /></label>
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold rounded-xl px-4 py-2.5 hover:-translate-y-0.5 transition-transform" data-testid="prompt-new-btn"><Plus size={16} /> New prompt</button>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-6 flex-wrap">
        <div className="flex items-center gap-2 bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 flex-1 min-w-[220px]">
          <Search size={15} className="text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, tag, description…" className="bg-transparent outline-none text-white text-sm w-full placeholder:text-slate-600" data-testid="prompt-search-input" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-300 outline-none" data-testid="prompt-category-filter">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-300 outline-none" data-testid="prompt-status-filter">
          <option value="">All statuses</option>
          <option value="approved">Approved</option>
          <option value="in_review">In review</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {prompts === null ? <p className="text-slate-600 text-sm mt-8">Loading…</p>
        : prompts.length === 0 ? <p className="text-slate-600 text-sm mt-8" data-testid="prompt-empty">No prompts match.</p> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {prompts.map((p) => (
            <Link key={p.id} to={`/os/prompts/${p.id}`} data-testid={`prompt-card-${p.id}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#0055FF]/40 hover:-translate-y-0.5 transition-all duration-300 group">
              <div className="flex items-center gap-2">
                <StatusBadge status={p.status} />
                {p.published_version && <span className="text-[10px] font-mono text-slate-500">live v{p.published_version}</span>}
                <span className="ml-auto text-[10px] font-mono text-[#00F0FF] uppercase">{p.category}</span>
              </div>
              <h3 className="font-display font-bold text-lg mt-3 text-white group-hover:text-[#00F0FF] transition-colors">{p.title}</h3>
              <p className="font-mono text-[11px] text-slate-500 mt-0.5">{p.name}</p>
              <p className="text-slate-400 text-sm mt-2 line-clamp-2">{p.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p.tags || []).slice(0, 4).map((t) => <span key={t} className="text-[10px] font-mono text-slate-400 border border-white/10 rounded-full px-2 py-0.5">{t}</span>)}
              </div>
            </Link>
          ))}
        </div>
      )}

      {showNew && <NewPromptModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} categories={cats} />}
    </div>
  );
}

function NewPromptModal({ onClose, onCreated, categories }) {
  const [f, setF] = useState({ name: "", title: "", description: "", category: "custom", surface: "arcaos", system_template: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.name || !f.title || !f.system_template) { toast.error("Name, title and system template are required"); return; }
    setBusy(true);
    try {
      const body = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      const r = await osFetch("/prompts", { method: "POST", body });
      toast.success("Prompt created as draft");
      onCreated(r);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" onClick={(e) => e.stopPropagation()} data-testid="prompt-new-modal">
        <div className="flex items-center justify-between mb-4"><h2 className="font-display font-bold text-xl">New prompt</h2><button onClick={onClose} className="text-slate-500 hover:text-white"><X size={18} /></button></div>
        <div className="space-y-3">
          <input value={f.name} onChange={set("name")} placeholder="name (e.g. arcacommerce/product-copy)" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none" data-testid="prompt-new-name" />
          <input value={f.title} onChange={set("title")} placeholder="Human title" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none" data-testid="prompt-new-title" />
          <div className="flex gap-3">
            <select value={f.category} onChange={set("category")} className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-300 outline-none" data-testid="prompt-new-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={f.surface} onChange={set("surface")} placeholder="surface" className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none" data-testid="prompt-new-surface" />
          </div>
          <input value={f.description} onChange={set("description")} placeholder="Description" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none" />
          <input value={f.tags} onChange={set("tags")} placeholder="tags, comma separated" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none" />
          <textarea value={f.system_template} onChange={set("system_template")} placeholder="System template — use {{variables}} for placeholders" rows={6} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none font-mono" data-testid="prompt-new-template" />
        </div>
        <button onClick={create} disabled={busy} className="mt-4 w-full bg-[#0055FF] text-white font-semibold rounded-xl py-3 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="prompt-new-create">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create draft</button>
      </div>
    </div>
  );
}
